import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery } from '@apollo/client';
import { createTool, getSignedUrl, getObjectUrl } from '../../graphql/mutation';
import { getCategories } from '../../graphql/queries';
import { 
  Card, 
  Form, 
  Input, 
  Select, 
  Upload, 
  Button, 
  Space, 
  Row, 
  Col,
  Image,
  InputNumber,
  Divider
} from 'antd';
import { UploadOutlined, PlusOutlined, MinusCircleOutlined } from '@ant-design/icons';
import { uploadToR2 } from '../../utils/uploadToR2';
import { showSnackBar } from '../../components/SnackBar/SnackBar';
import './Studios.css';

const { Option } = Select;
const { TextArea } = Input;

const imageTypes = [
  { value: 'DUAL', label: 'Dual' },
  { value: 'SINGLE', label: 'Single' },
];

const systemProviders = [
  { value: 'WIRO_PX', label: 'Wiro PX' },
  { value: 'WIRO_KLING', label: 'Wiro Kling' },
  { value: 'WIRO_MINIMAX', label: 'Wiro Minimax' },
  { value: 'WIRO_BYTEDANCE', label: 'Wiro Bytedance' },
  { value: 'FAL', label: 'Fal' },
];

const CreateTool = () => {
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [files, setFiles] = useState({
    banner: null,
    video: null,
    webm: null,
    webp: null,
  });
  const [previews, setPreviews] = useState({
    banner: null,
    video: null,
    webm: null,
    webp: null,
  });
  const [loading, setLoading] = useState(false);

  const { data: categoriesData } = useQuery(getCategories, {
    variables: { pageSize: 100, page: 1 }
  });

  const [createToolMutation] = useMutation(createTool);
  const [getSignedUrlMutation] = useMutation(getSignedUrl);
  const [getObjectUrlMutation] = useMutation(getObjectUrl);

  const handleFileChange = (type, file) => {
    if (file) {
      const isLt100M = file.size / 1024 / 1024 < 100;
      if (!isLt100M) {
        showSnackBar('error', 'File Too Large', `${type} must be smaller than 100MB`);
        return false;
      }

      // Validate file types
      if (type === 'banner' && !file.type.startsWith('image/')) {
        showSnackBar('error', 'Invalid File Type', 'Banner must be an image file');
        return false;
      }

      if (type === 'video' && !file.type.startsWith('video/')) {
        showSnackBar('error', 'Invalid File Type', 'Video must be a video file');
        return false;
      }

      if (type === 'webm' && file.type !== 'video/webm') {
        showSnackBar('error', 'Invalid File Type', 'WebM file must be a .webm video file');
        return false;
      }

      if (type === 'webp' && file.type !== 'image/webp') {
        showSnackBar('error', 'Invalid File Type', 'WebP file must be a .webp image file');
        return false;
      }

      setFiles(prev => ({ ...prev, [type]: file }));
      setPreviews(prev => ({ 
        ...prev, 
        [type]: URL.createObjectURL(file) 
      }));
      showSnackBar('success', 'File Selected', `${type} file selected successfully`);
    }
    return false;
  };

  const getSignedUrls = async () => {
    const urls = {};
    for (const [type, file] of Object.entries(files)) {
      if (file) {
        const { data } = await getSignedUrlMutation({
          variables: { fileName: file.name }
        });
        urls[type] = data.getSignedUrl;
      }
    }
    return urls;
  };

  const handleSubmit = async (values) => {
    try {
      setLoading(true);
      
      // Validate required files
      if (!files.banner) {
        showSnackBar('error', 'Missing Banner', 'Banner image is required');
        return;
      }

      const signedUrls = await getSignedUrls();
      
      // Upload files - only upload files that exist
      const uploadPromises = Object.entries(signedUrls)
        .filter(([type, urlData]) => files[type] !== null)
        .map(([type, urlData]) => 
          uploadToR2(files[type], urlData.url, urlData.contentType)
        );
      
      if (uploadPromises.length > 0) {
        showSnackBar('info', 'Uploading Files', 'Please wait while files are being uploaded...');
        await Promise.all(uploadPromises);
      }

      // Get permanent URLs - only for files that were uploaded
      const objectUrls = {};
      for (const [type, urlData] of Object.entries(signedUrls)) {
        if (files[type] !== null) {
          const { data } = await getObjectUrlMutation({
            variables: { key: urlData.id, eTag: 'etag' }
          });
          objectUrls[type] = data.getObjectUrl;
        }
      }

      // Filter aspect ratios to only include fields accepted by ToolRatioInput
      const cleanAspectRatios = values.aspectRatios ? values.aspectRatios.map(ratio => ({
        width: ratio.width,
        height: ratio.height,
        name: ratio.name,
        aspectRatioX: ratio.aspectRatioX,
        aspectRatioY: ratio.aspectRatioY,
      })) : [];

      // Create tool with input object
      const input = {
        name: values.name,
        imageType: values.imageType,
        bannerUrl: objectUrls.banner,
        videoUrl: objectUrls.video || null,
        webmUrl: objectUrls.webm || null,
        webpUrl: objectUrls.webp || null,
        templateId: values.templateId && values.templateId.trim() !== '' ? values.templateId.trim() : null,
        prompt: values.prompt || null,
        isFeatured: values.isFeatured || false,
        duration: values.duration || null,
        cost: values.cost || null,
        aspectRatios: cleanAspectRatios,
        categoryId: values.categoryId || null,
        provider: values.provider,
      };

      await createToolMutation({
        variables: { input }
      });

      showSnackBar('success', 'Success', 'Tool created successfully');
      navigate('/tools');
    } catch (error) {
      showSnackBar('error', 'Error', error.message || 'Failed to create tool');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="studios-container">
      <Card>
        <div className="table-header">
          <h2>Create Tool</h2>
          <Button onClick={() => navigate('/tools')}>
            Back to List
          </Button>
        </div>

        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          initialValues={{
            isFeatured: false,
            duration: 3,
            aspectRatios: [
              {
                width: 1920,
                height: 1080,
                name: 'landscape',
                aspectRatioX: 16,
                aspectRatioY: 9,
              }
            ],
          }}
        >
          <Row gutter={24}>
            <Col span={12}>
              <Form.Item
                name="name"
                label="Tool Name"
                rules={[{ required: true, message: 'Please enter tool name' }]}
              >
                <Input placeholder="Enter tool name" />
              </Form.Item>

              <Form.Item
                name="imageType"
                label="Image Type"
                rules={[{ required: true, message: 'Please select image type' }]}
              >
                <Select placeholder="Select image type">
                  {imageTypes.map(type => (
                    <Option key={type.value} value={type.value}>
                      {type.label}
                    </Option>
                  ))}
                </Select>
              </Form.Item>

              <Form.Item
                name="templateId"
                label="Template ID"
              >
                <Input
                  style={{ width: '100%' }}
                  placeholder="Enter template ID (optional)"
                />
              </Form.Item>

              <Form.Item
                name="duration"
                label="Duration (seconds)"
              >
                <InputNumber
                  min={1}
                  max={30}
                  style={{ width: '100%' }}
                  placeholder="Enter duration in seconds"
                />
              </Form.Item>

              <Form.Item
                name="cost"
                label="Cost ($)"
              >
                <InputNumber
                  min={0}
                  step={0.01}
                  precision={2}
                  style={{ width: '100%' }}
                  placeholder="Enter cost in dollars"
                  controls={false}
                />
              </Form.Item>

              <Form.Item
                name="isFeatured"
                label="Featured"
              >
                <Select placeholder="Is this tool featured?">
                  <Option value={true}>Yes</Option>
                  <Option value={false}>No</Option>
                </Select>
              </Form.Item>

              <Form.Item
                name="categoryId"
                label="Category"
              >
                <Select placeholder="Select category (optional)" allowClear>
                  {categoriesData?.getCategories?.map(category => (
                    <Option key={category.id} value={category.id}>
                      {category.name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>

              <Form.Item
                name="provider"
                label="Provider"
                rules={[{ required: true, message: 'Please select a provider' }]}
              >
                <Select placeholder="Select provider">
                  {systemProviders.map(provider => (
                    <Option key={provider.value} value={provider.value}>
                      {provider.label}
                    </Option>
                  ))}
                </Select>
              </Form.Item>

              <Form.Item
                name="prompt"
                label="Prompt"
              >
                <TextArea 
                  rows={4} 
                  placeholder="Enter prompt (optional)"
                />
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item label="Aspect Ratios">
                <Form.List name="aspectRatios">
                  {(fields, { add, remove }) => (
                    <div style={{ border: '1px solid #f0f0f0', borderRadius: 6, padding: 16 }}>
                      {fields.map(({ key, name, ...restField }) => (
                        <Row key={key} gutter={8} style={{ marginBottom: 12, alignItems: 'center' }}>
                          <Col span={5}>
                            <Form.Item
                              {...restField}
                              name={[name, 'name']}
                              style={{ marginBottom: 0 }}
                              rules={[{ required: true, message: 'Required' }]}
                            >
                              <Input placeholder="Name" size="small" />
                            </Form.Item>
                          </Col>
                          <Col span={4}>
                            <Form.Item
                              {...restField}
                              name={[name, 'width']}
                              style={{ marginBottom: 0 }}
                              rules={[{ required: true, message: 'Required' }]}
                            >
                              <InputNumber 
                                min={1} 
                                style={{ width: '100%' }}
                                placeholder="W"
                                size="small"
                                controls={false}
                              />
                            </Form.Item>
                          </Col>
                          <Col span={1} style={{ textAlign: 'center', fontSize: 12, color: '#999' }}>
                            ×
                          </Col>
                          <Col span={4}>
                            <Form.Item
                              {...restField}
                              name={[name, 'height']}
                              style={{ marginBottom: 0 }}
                              rules={[{ required: true, message: 'Required' }]}
                            >
                              <InputNumber 
                                min={1} 
                                style={{ width: '100%' }}
                                placeholder="H"
                                size="small"
                                controls={false}
                              />
                            </Form.Item>
                          </Col>
                          <Col span={4}>
                            <Form.Item
                              {...restField}
                              name={[name, 'aspectRatioX']}
                              style={{ marginBottom: 0 }}
                              rules={[{ required: true, message: 'Required' }]}
                            >
                              <InputNumber 
                                min={1} 
                                style={{ width: '100%' }}
                                placeholder="X"
                                size="small"
                                controls={false}
                              />
                            </Form.Item>
                          </Col>
                          <Col span={1} style={{ textAlign: 'center', fontSize: 12, color: '#999' }}>
                            :
                          </Col>
                          <Col span={4}>
                            <Form.Item
                              {...restField}
                              name={[name, 'aspectRatioY']}
                              style={{ marginBottom: 0 }}
                              rules={[{ required: true, message: 'Required' }]}
                            >
                              <InputNumber 
                                min={1} 
                                style={{ width: '100%' }}
                                placeholder="Y"
                                size="small"
                                controls={false}
                              />
                            </Form.Item>
                          </Col>
                          <Col span={1}>
                            {fields.length > 1 && (
                              <Button
                                type="text"
                                icon={<MinusCircleOutlined />}
                                onClick={() => remove(name)}
                                size="small"
                                danger
                                style={{ color: '#ff4d4f' }}
                              />
                            )}
                          </Col>
                        </Row>
                      ))}
                      
                      <Button
                        type="dashed"
                        onClick={() => add()}
                        icon={<PlusOutlined />}
                        size="small"
                        style={{ marginTop: 8 }}
                      >
                        Add Ratio
                      </Button>
                    </div>
                  )}
                </Form.List>
              </Form.Item>

              <Divider orientation="left">Media Files</Divider>

              <Form.Item label="Banner Image (Required)">
                <Upload
                  beforeUpload={(file) => handleFileChange('banner', file)}
                  accept="image/*"
                  showUploadList={false}
                >
                  <Button icon={<UploadOutlined />}>
                    Select Banner Image
                  </Button>
                </Upload>
                {previews.banner && (
                  <div style={{ marginTop: 10 }}>
                    <Image
                      src={previews.banner}
                      width={100}
                      height={60}
                      style={{ objectFit: 'cover', borderRadius: 4 }}
                    />
                  </div>
                )}
              </Form.Item>

              <Form.Item label="Video File (Optional)">
                <Upload
                  beforeUpload={(file) => handleFileChange('video', file)}
                  accept="video/*"
                  showUploadList={false}
                >
                  <Button icon={<UploadOutlined />}>
                    Select Video File
                  </Button>
                </Upload>
                {previews.video && (
                  <div style={{ marginTop: 10 }}>
                    <video
                      src={previews.video}
                      width={150}
                      height={90}
                      controls
                      style={{ borderRadius: 4 }}
                    />
                  </div>
                )}
              </Form.Item>

              <Form.Item label="WebM File (Optional)">
                <Upload
                  beforeUpload={(file) => handleFileChange('webm', file)}
                  accept=".webm,video/webm"
                  showUploadList={false}
                >
                  <Button icon={<UploadOutlined />}>
                    Select WebM File
                  </Button>
                </Upload>
                {previews.webm && (
                  <div style={{ marginTop: 10 }}>
                    <video
                      src={previews.webm}
                      width={150}
                      height={90}
                      controls
                      style={{ borderRadius: 4 }}
                    />
                  </div>
                )}
              </Form.Item>

              <Form.Item label="WebP Image (Optional)">
                <Upload
                  beforeUpload={(file) => handleFileChange('webp', file)}
                  accept=".webp,image/webp"
                  showUploadList={false}
                >
                  <Button icon={<UploadOutlined />}>
                    Select WebP Image
                  </Button>
                </Upload>
                {previews.webp && (
                  <div style={{ marginTop: 10 }}>
                    <Image
                      src={previews.webp}
                      width={100}
                      height={60}
                      style={{ objectFit: 'cover', borderRadius: 4 }}
                    />
                  </div>
                )}
              </Form.Item>
            </Col>
          </Row>

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit" loading={loading}>
                Create Tool
              </Button>
              <Button onClick={() => navigate('/tools')}>
                Cancel
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Card>
    </div>
  );
};

export default CreateTool; 